// Copyright ©️ 2026 eVolve MEP, LLC

import './nav.css';

import { useEffect, useMemo, useState } from 'react';

import { ActionIcon, Flex, Stack } from '@mantine/core';

import { EvolveLogoLink } from 'app/Layout/HeaderBar/components/EvolveLogoLink';
import { GLOBAL_NAVIGATION_MENU } from 'app/Layout/NavBar/globalNavigationMenu';
import { OverlayScrollbar } from 'app/Layout/NavBar/NavBarContent/CustomScrollbar';
import { NavBarAdSpot } from 'app/Layout/NavBar/NavBarContent/NavBarAdSpot';
import { NavBarFooter, type NavbarFooterItemKey } from 'app/Layout/NavBar/NavBarContent/NavBarFooter';
import { NavBarSection } from 'app/Layout/NavBar/NavBarContent/NavBarSection';
import { useCanAccessRoute } from 'app/Layout/NavBar/NavBarContent/useCanAccessRoute';
import {
  navbarAnimationDuration,
  navbarAnimationTimingFunction,
  navbarTransitionStyle,
  useAppShell,
} from 'app/Layout/NavBar/useAppShell';
import { ALL_MODULE_ROUTES } from 'app/ModuleRoutes';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNotNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';
import { WithColorScheme } from 'styles/theme/ColorScheme';

import styledNavLinkClasses from './StyledNavLink.module.css';

export const NavBarContent = () => {
  const { setNavbarExpanded, navbarExpanded, navbarWidth } = useAppShell();
  const isMobile = useIsMobile();
  const canAccessRoute = useCanAccessRoute();

  const items = useMemo(
    () =>
      GLOBAL_NAVIGATION_MENU.map((item) => ({
        ...item,
        options: item.options.filter(
          ({ route }) => canAccessRoute(route) && (!isMobile || isKeyOf('mobile', ALL_MODULE_ROUTES[route])),
        ),
      })).filter((item) => item.options.length > 0),
    [canAccessRoute, isMobile],
  );

  // To prevent menus from hovering over each other, the menus share a state here so only one can open at a time.
  // The footer has slightly different behavior. It's always a Menu even when the navbar is open.
  const [activePopover, setActivePopover] = useState<
    | { location: 'navbar'; value: (typeof GLOBAL_NAVIGATION_MENU)[number]['label'] }
    | { location: 'footer'; value: NavbarFooterItemKey }
    | null
  >(null);
  // If the navbar is opened and there's a non-footer menu open, close it automatically.
  useEffect(() => {
    if (navbarExpanded) {
      setActivePopover((s) => (s?.location === 'navbar' ? null : s));
    }
  }, [navbarExpanded]);

  return (
    <WithColorScheme
      colorScheme="dark"
      style={{
        width: navbarWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        transition: navbarTransitionStyle,
      }}
    >
      <Flex direction="column" flex="1 0 0" gap="xs" style={{ transition: navbarTransitionStyle }}>
        {/* On mobile the logo already lives in the always-visible header, so it's omitted here. */}
        {!isMobile && (
          <>
            <Flex
              justify="space-between"
              align="center"
              ml="xs"
              mt="sm"
              h={40}
              opacity={navbarExpanded ? 1 : 0}
              style={{ transition: navbarTransitionStyle }}
            >
              <EvolveLogoLink disabled={!navbarExpanded} />
            </Flex>
            <ActionIcon
              className={styledNavLinkClasses.navLink}
              size="input-sm"
              onClick={() => setNavbarExpanded(!navbarExpanded)}
              variant="transparent"
              style={{
                position: 'absolute',
                top: 22,
                left: navbarExpanded ? navbarWidth - 40 : navbarWidth / 2 - 12,
                transition: `left ${navbarAnimationDuration}ms ${navbarAnimationTimingFunction}`,
              }}
            >
              <EvolveIcon icon="NavBarMenu" />
            </ActionIcon>
          </>
        )}

        <OverlayScrollbar
          className={navbarExpanded ? undefined : 'nav-bar-collapsed'}
          style={{ flex: '1 1 0', transition: navbarTransitionStyle }}
        >
          <Stack px={2} gap={4} className="nav-container-contents">
            {items.map((item) => (
              <NavBarSection
                key={item.label}
                navData={item}
                navbarExpanded={navbarExpanded}
                popoverOpened={activePopover?.location === 'navbar' && activePopover.value === item.label}
                onPopoverChanged={(popoverOpened) =>
                  setActivePopover(popoverOpened ? { location: 'navbar', value: item.label } : null)
                }
              />
            ))}
          </Stack>
        </OverlayScrollbar>
      </Flex>

      <NavBarAdSpot />
      <NavBarFooter
        activePopover={activePopover?.location === 'footer' ? activePopover.value : null}
        onPopoverChanged={(key) => setActivePopover(isNotNil(key) ? { location: 'footer', value: key } : null)}
      />
    </WithColorScheme>
  );
};
